import { Share2, Facebook, Link2, Check } from "lucide-react";
import { useState } from "react";

// X (formerly Twitter) logo icon
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

/**
 * ShareButtons - Social sharing component
 * @param {string} url - URL to share (defaults to current page)
 * @param {string} title - Title for sharing
 * @param {string} description - Description for sharing
 * @param {string} variant - 'inline' (horizontal) or 'dropdown' (button with menu)
 */
export const ShareButtons = ({ 
  url, 
  title = "Check this out!", 
  description = "",
  variant = "inline" 
}) => {
  const [copied, setCopied] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  
  const shareUrl = url || window.location.href;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedDesc = encodeURIComponent(description);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLinks = [
    {
      name: "X",
      icon: XIcon,
      url: `https://x.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      color: "hover:text-white"
    },
    {
      name: "Facebook",
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: "hover:text-blue-500"
    }
  ];

  // Native share if available
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error("Share failed:", err);
        }
      }
    } else {
      setShowMenu(!showMenu);
    }
  };

  if (variant === "dropdown") {
    return (
      <div className="relative">
        <button
          onClick={handleNativeShare}
          className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white text-sm transition-colors rounded"
          data-testid="share-button"
        >
          <Share2 className="w-4 h-4" />
          Share
        </button>

        {showMenu && !navigator.share && (
          <div className="absolute right-0 top-full mt-2 w-48 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50">
            {shareLinks.map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-3 px-4 py-3 text-zinc-400 ${link.color} transition-colors border-b border-zinc-800 last:border-0`}
                onClick={() => setShowMenu(false)}
              >
                <link.icon className="w-4 h-4" />
                {link.name}
              </a>
            ))}
            <button
              onClick={() => { copyToClipboard(); setShowMenu(false); }}
              className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-white transition-colors w-full text-left"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
              {copied ? "Copied!" : "Copy Link"}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Inline variant
  return (
    <div className="flex items-center gap-2" data-testid="share-buttons">
      {shareLinks.map((link) => (
        <a
          key={link.name}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`p-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 ${link.color} transition-colors rounded`}
          title={`Share on ${link.name}`}
          data-testid={`share-${link.name.toLowerCase()}`}
        >
          <link.icon className="w-4 h-4" />
        </a>
      ))}
      <button
        onClick={copyToClipboard}
        className="p-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors rounded"
        title="Copy Link"
        data-testid="share-copy"
      >
        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
      </button>
    </div>
  );
};

export default ShareButtons;
